import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BANDS_SELECTED_CHAT_STORAGE_KEY } from "../../../../constants/bands";
import {
    BANDS_ACTIVE_APP_STORAGE_KEY,
    BANDS_COMMENT_PANEL_STORAGE_KEY,
    BANDS_WORKSPACE_APP_TAB_IDS,
    EMPTY_COMMENT_PANEL_MEMORY,
} from "../constants";

const readSelectedChatFromStorage = () => {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(BANDS_SELECTED_CHAT_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {
            return null;
        }
        if (parsed.type === "channel") {
            const groupId = parsed.groupId ?? parsed.group ?? parsed.groupID;
            const channelId = parsed.channelId ?? parsed.channel ?? parsed.channelID;
            if (groupId !== undefined && groupId !== null && channelId !== undefined && channelId !== null) {
                return {
                    type: "channel",
                    groupId: String(groupId),
                    channelId: String(channelId),
                };
            }
        } else if (parsed.type === "dm") {
            const directChatId = parsed.directChatId ?? parsed.chatId ?? parsed.id;
            if (directChatId !== undefined && directChatId !== null) {
                return {
                    type: "dm",
                    directChatId: String(directChatId),
                };
            }
        }
    } catch (error) {
        console.warn("Unable to restore bands chat selection", error);
    }
    return null;
};

const readActiveAppMapFromStorage = () => {
    if (typeof window === "undefined") {
        return {};
    }
    try {
        const raw = window.localStorage.getItem(BANDS_ACTIVE_APP_STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {
            return {};
        }
        return Object.entries(parsed).reduce((acc, [key, value]) => {
            if (typeof value === "string" && BANDS_WORKSPACE_APP_TAB_IDS.includes(value)) {
                acc[String(key)] = value;
            }
            return acc;
        }, {});
    } catch (error) {
        console.warn("Unable to read workspace tab preferences", error);
        return {};
    }
};

const readCommentPanelStateFromStorage = () => {
    if (typeof window === "undefined") {
        return { ...EMPTY_COMMENT_PANEL_MEMORY };
    }
    try {
        const raw = window.localStorage.getItem(BANDS_COMMENT_PANEL_STORAGE_KEY);
        if (!raw) {
            return { ...EMPTY_COMMENT_PANEL_MEMORY };
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {
            return { ...EMPTY_COMMENT_PANEL_MEMORY };
        }
        const scope = typeof parsed.scope === "string" ? parsed.scope : null;
        const messageId = parsed.messageId ?? parsed.messageID;
        if (!scope || !messageId) {
            return { ...EMPTY_COMMENT_PANEL_MEMORY };
        }
        if (scope === "channel") {
            const groupId = parsed.groupId ?? parsed.group ?? parsed.groupID;
            const channelId = parsed.channelId ?? parsed.channel ?? parsed.channelID;
            if (groupId && channelId) {
                return {
                    open: true,
                    scope: "channel",
                    groupId: String(groupId),
                    channelId: String(channelId),
                    directChatId: null,
                    messageId: String(messageId),
                };
            }
        } else if (scope === "dm") {
            const directChatId = parsed.directChatId ?? parsed.chatId ?? parsed.id;
            if (directChatId) {
                return {
                    open: true,
                    scope: "dm",
                    groupId: null,
                    channelId: null,
                    directChatId: String(directChatId),
                    messageId: String(messageId),
                };
            }
        }
    } catch (error) {
        console.warn("Unable to restore comment panel state", error);
    }
    return { ...EMPTY_COMMENT_PANEL_MEMORY };
};

export default function useBandsWorkspaceUiState({
    activeApp,
    selectedChannelId,
    selectedDirectChatId,
    selectedGroupId,
    setActiveApp,
}) {
    const initialChatSelectionRef = useRef(readSelectedChatFromStorage());
    const activeAppMapRef = useRef(readActiveAppMapFromStorage());
    const [commentPanelMemory, setCommentPanelMemory] = useState(() => readCommentPanelStateFromStorage());

    useEffect(() => {
        if (!selectedGroupId) {
            setActiveApp((prev) => (prev === "chat" ? prev : "chat"));
            return;
        }
        const key = String(selectedGroupId);
        const stored = activeAppMapRef.current && typeof activeAppMapRef.current === "object"
            ? activeAppMapRef.current[key]
            : undefined;
        if (typeof stored === "string" && BANDS_WORKSPACE_APP_TAB_IDS.includes(stored)) {
            setActiveApp((prev) => (prev === stored ? prev : stored));
        } else {
            setActiveApp((prev) => (prev === "chat" ? prev : "chat"));
        }
    }, [selectedGroupId, setActiveApp]);

    const channelCommentPanelState = useMemo(() => {
        if (!commentPanelMemory.open || commentPanelMemory.scope !== "channel") {
            return null;
        }
        if (!selectedGroupId || !selectedChannelId) {
            return null;
        }
        if (String(commentPanelMemory.groupId) !== String(selectedGroupId)) {
            return null;
        }
        if (String(commentPanelMemory.channelId) !== String(selectedChannelId)) {
            return null;
        }
        if (!commentPanelMemory.messageId) {
            return null;
        }
        return {
            open: true,
            messageId: commentPanelMemory.messageId,
        };
    }, [commentPanelMemory, selectedChannelId, selectedGroupId]);

    const directCommentPanelState = useMemo(() => {
        if (!commentPanelMemory.open || commentPanelMemory.scope !== "dm") {
            return null;
        }
        if (!selectedDirectChatId) {
            return null;
        }
        if (String(commentPanelMemory.directChatId) !== String(selectedDirectChatId)) {
            return null;
        }
        if (!commentPanelMemory.messageId) {
            return null;
        }
        return {
            open: true,
            messageId: commentPanelMemory.messageId,
        };
    }, [commentPanelMemory, selectedDirectChatId]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        try {
            if (selectedDirectChatId != null) {
                const payload = {
                    type: "dm",
                    directChatId: String(selectedDirectChatId),
                };
                window.localStorage.setItem(BANDS_SELECTED_CHAT_STORAGE_KEY, JSON.stringify(payload));
                return;
            }
            if (selectedGroupId != null && selectedChannelId != null) {
                const payload = {
                    type: "channel",
                    groupId: String(selectedGroupId),
                    channelId: String(selectedChannelId),
                };
                window.localStorage.setItem(BANDS_SELECTED_CHAT_STORAGE_KEY, JSON.stringify(payload));
            } else {
                window.localStorage.removeItem(BANDS_SELECTED_CHAT_STORAGE_KEY);
            }
        } catch (error) {
            console.warn("Unable to persist chat selection", error);
        }
    }, [selectedGroupId, selectedChannelId, selectedDirectChatId]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        if (!selectedGroupId) {
            return;
        }
        if (!BANDS_WORKSPACE_APP_TAB_IDS.includes(activeApp)) {
            return;
        }
        const key = String(selectedGroupId);
        const currentMap = activeAppMapRef.current && typeof activeAppMapRef.current === "object"
            ? activeAppMapRef.current
            : {};
        if (currentMap[key] === activeApp) {
            return;
        }
        const nextMap = { ...currentMap, [key]: activeApp };
        activeAppMapRef.current = nextMap;
        try {
            window.localStorage.setItem(BANDS_ACTIVE_APP_STORAGE_KEY, JSON.stringify(nextMap));
        } catch (error) {
            console.warn("Unable to persist workspace tab", error);
        }
    }, [activeApp, selectedGroupId]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        try {
            if (!commentPanelMemory.open || !commentPanelMemory.messageId) {
                window.localStorage.removeItem(BANDS_COMMENT_PANEL_STORAGE_KEY);
                return;
            }
            if (commentPanelMemory.scope === "channel") {
                if (!commentPanelMemory.groupId || !commentPanelMemory.channelId) {
                    window.localStorage.removeItem(BANDS_COMMENT_PANEL_STORAGE_KEY);
                    return;
                }
                const payload = {
                    open: true,
                    scope: "channel",
                    groupId: String(commentPanelMemory.groupId),
                    channelId: String(commentPanelMemory.channelId),
                    messageId: String(commentPanelMemory.messageId),
                };
                window.localStorage.setItem(BANDS_COMMENT_PANEL_STORAGE_KEY, JSON.stringify(payload));
                return;
            }
            if (commentPanelMemory.scope === "dm") {
                if (!commentPanelMemory.directChatId) {
                    window.localStorage.removeItem(BANDS_COMMENT_PANEL_STORAGE_KEY);
                    return;
                }
                const payload = {
                    open: true,
                    scope: "dm",
                    directChatId: String(commentPanelMemory.directChatId),
                    messageId: String(commentPanelMemory.messageId),
                };
                window.localStorage.setItem(BANDS_COMMENT_PANEL_STORAGE_KEY, JSON.stringify(payload));
                return;
            }
            window.localStorage.removeItem(BANDS_COMMENT_PANEL_STORAGE_KEY);
        } catch (error) {
            console.warn("Unable to persist comment panel state", error);
        }
    }, [commentPanelMemory]);

    const handleChannelCommentPanelChange = useCallback((nextState) => {
        if (!selectedGroupId || !selectedChannelId) {
            return;
        }
        const messageId = nextState?.messageId != null ? String(nextState.messageId) : null;
        const shouldOpen = Boolean(nextState?.open && messageId);
        setCommentPanelMemory((previous) => {
            if (!shouldOpen) {
                if (!previous.open) {
                    return previous;
                }
                const matches = previous.scope === "channel"
                    && String(previous.groupId) === String(selectedGroupId)
                    && String(previous.channelId) === String(selectedChannelId);
                if (!matches) {
                    return previous;
                }
                return { ...EMPTY_COMMENT_PANEL_MEMORY };
            }
            const next = {
                open: true,
                scope: "channel",
                groupId: String(selectedGroupId),
                channelId: String(selectedChannelId),
                directChatId: null,
                messageId,
            };
            if (
                previous.open
                && previous.scope === "channel"
                && String(previous.groupId) === next.groupId
                && String(previous.channelId) === next.channelId
                && String(previous.messageId) === next.messageId
            ) {
                return previous;
            }
            return next;
        });
    }, [selectedChannelId, selectedGroupId]);

    const handleDirectCommentPanelChange = useCallback((nextState) => {
        if (!selectedDirectChatId) {
            return;
        }
        const messageId = nextState?.messageId != null ? String(nextState.messageId) : null;
        const shouldOpen = Boolean(nextState?.open && messageId);
        setCommentPanelMemory((previous) => {
            if (!shouldOpen) {
                if (!previous.open) {
                    return previous;
                }
                const matches = previous.scope === "dm"
                    && String(previous.directChatId) === String(selectedDirectChatId);
                if (!matches) {
                    return previous;
                }
                return { ...EMPTY_COMMENT_PANEL_MEMORY };
            }
            const next = {
                open: true,
                scope: "dm",
                groupId: null,
                channelId: null,
                directChatId: String(selectedDirectChatId),
                messageId,
            };
            if (
                previous.open
                && previous.scope === "dm"
                && String(previous.directChatId) === next.directChatId
                && String(previous.messageId) === next.messageId
            ) {
                return previous;
            }
            return next;
        });
    }, [selectedDirectChatId]);

    return {
        channelCommentPanelState,
        directCommentPanelState,
        handleChannelCommentPanelChange,
        handleDirectCommentPanelChange,
        initialChatSelectionRef,
    };
}
